import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, Loader2, CheckCircle2, AlertCircle, RotateCcw, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import axios from 'axios';

const compressImage = (file, maxWidth = 1600) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = Math.round(h * maxWidth / w); w = maxWidth; }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.85);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const ScanCarteGrisePage = () => {
  const { sessionId } = useParams();
  const [status, setStatus] = useState('ready');
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const handleCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setStatus('uploading');
    setError('');
    setProgress('Compression de la photo...');

    try {
      const compressed = await compressImage(file);
      setProgress(`Envoi (${(compressed.size / 1024).toFixed(0)} Ko)...`);

      const formData = new FormData();
      formData.append('file', compressed);

      setStatus('processing');
      setProgress('Analyse IA en cours...');

      await axios.post(`${API}/scan-sessions/${sessionId}/upload`, formData, {
        timeout: 60000,
      });
      setStatus('success');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Erreur inconnue';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Timeout - la photo met trop de temps. Reessayez avec une meilleure lumiere.');
      } else if (err.response?.status === 404) {
        setError('Session expiree. Demandez un nouveau QR code.');
      } else {
        setError(msg);
      }
      setStatus('error');
    }
  };

  const retry = () => {
    setStatus('ready');
    setError('');
    setProgress('');
    if (cameraRef.current) cameraRef.current.value = '';
    if (galleryRef.current) galleryRef.current.value = '';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900">Vortekio</h1>
          <p className="text-sm text-slate-500 mt-1">Scanner la carte grise</p>
        </div>

        {status === 'ready' && (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed border-indigo-300 rounded-2xl p-8 text-center bg-white cursor-pointer hover:border-indigo-500 hover:bg-indigo-50/50 transition-all active:scale-95"
              onClick={() => cameraRef.current?.click()}
              data-testid="capture-area"
            >
              <Camera className="w-16 h-16 mx-auto text-indigo-500 mb-4" />
              <p className="text-lg font-semibold text-slate-800">Prendre la photo</p>
              <p className="text-sm text-slate-500 mt-1">Appuyez pour ouvrir l'appareil photo</p>
            </div>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleCapture} data-testid="mobile-scan-input" />

            <div className="text-center">
              <button
                type="button"
                onClick={() => galleryRef.current?.click()}
                className="text-sm text-indigo-600 underline hover:text-indigo-800"
                data-testid="gallery-btn"
              >
                <ImageIcon className="w-4 h-4 inline mr-1" />
                Choisir depuis la galerie
              </button>
              <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleCapture} data-testid="gallery-input" />
            </div>

            <p className="text-xs text-center text-slate-400">
              Positionnez la carte grise bien a plat et assurez-vous que tous les champs sont visibles
            </p>
          </div>
        )}

        {(status === 'uploading' || status === 'processing') && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-slate-200">
            <Loader2 className="w-16 h-16 mx-auto text-indigo-500 animate-spin mb-4" />
            <p className="text-lg font-semibold text-slate-800">
              {status === 'uploading' ? 'Envoi de la photo...' : 'Analyse en cours...'}
            </p>
            <p className="text-sm text-slate-500 mt-1">{progress || "L'IA lit les donnees de la carte grise"}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-emerald-200">
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
            <p className="text-lg font-semibold text-slate-800">Scan reussi !</p>
            <p className="text-sm text-slate-500 mt-1">
              Les donnees ont ete envoyees au formulaire.<br />Vous pouvez fermer cette page.
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-red-200">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <p className="text-lg font-semibold text-slate-800">Erreur</p>
            <p className="text-sm text-red-600 mt-1">{error}</p>
            <Button onClick={retry} className="mt-4" variant="outline" data-testid="retry-scan-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Reessayer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanCarteGrisePage;
